import { Component, HostBinding, Input, OnInit } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment-timezone';

import { BankAccountsService } from '../../../../services/financial-accounts/bank-accounts.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { OrganizationService } from '../../../../services/organization/organization.service';
import { PaymentMethodsService } from '../../../../services/financial-accounts/payment-methods.service';

import { DatatableConfig } from '../../../shared/datatable/datatable.config';
import { DepositBankAccount } from '../../../../models/entities/deposit-bank-account';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { Organization } from '../../../../models/entities/organization';
import { ChargesService } from '../../../../services/charges/charges.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UnifiedBankAccount } from '../../../../models/entities/unified-bank-account';
import { PlaidService } from '../../../../services/plaid.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AddOrgBankAccountDialogComponent, AddOrgBankAccountDialogResult } from '../bank-account-manager/add-org-bank-account-dialog/add-org-bank-account-dialog.component';
import { GrowlerService } from '../../../../services/growler.service';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';

declare var Plaid: any;

@UntilDestroy()
@Component({
    providers: [NgbPopover],
    selector: 'lf-org-bank-account-list',
    styleUrls: ['./org-bank-account-list.scss'],
    templateUrl: './org-bank-account-list.component.html',
})
export class OrgBankAccountListComponent implements OnInit
{
    @HostBinding('class.card') card = true;
    @HostBinding('class.card-set-height') cardSetHeight = true;
    plaidObject: any = null;
    @Input() bankAccounts: UnifiedBankAccount[] = null;

    organization: Organization = null;
    isShowingPendingFundsDialog = false;
    pendingDetailAccount: DepositBankAccount = null;
    isPendingFundsLoading = false;
    pendingFundsConfig: DatatableConfig = {
        columns: [
            {title: 'Payment Date', key: 'createdAt', sort: 'desc'},
            {
                title: 'Payor',
                key:
                        'paymentGroup.payor.name|paymentGroup.payor.profile.familyName',
            },
            {
                title: 'Payment Method',
                key: 'paymentGroup.paymentMethodType',
                sort: false,
            },
            {title: 'Payment Amount', key: 'applied'},
            {title: 'Net Amount', key: 'net'},
            {title: 'Funds Available', key: 'fundsAvailable'},
        ],
        filtering: {filter: ''},
    };
    unifiedLink: number;
    isShowingBankAccountTipModal = false;
    jwt: JwtLegFiClaims = null;
    isLoadingAccounts = true;
    hasWriteAccess = false;

    constructor(
            private _bankAccountsService: BankAccountsService,
            private _chargesService: ChargesService,
            private _paymentMethodsService: PaymentMethodsService,
            private _organizationService: OrganizationService,
            private _plaidService: PlaidService,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
    ) {
        // This alias var is here purely to get rid of a compiler error by hacking the typedef.
        const trix: any = moment;
        trix.updateLocale('en', {
            relativeTime: {
                future: 'just now',
            },
        });
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();
        this.hasWriteAccess = LegFiJwtService.doesUserHaveModulePermission('transactions', true);
        if (this.bankAccounts === null) {
            this.loadBankAccounts();
        } else {
            this.isLoadingAccounts = false;
        }
    }

    loadBankAccounts() {
        this.isLoadingAccounts = true;
        this._bankAccountsService.getOrgBankAccounts(this.jwt.orgId)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (accounts: UnifiedBankAccount[]) => {
                        this.bankAccounts = accounts.filter((uba: UnifiedBankAccount) => {
                            return uba.depositBankAccount;
                        });
                        this.isLoadingAccounts = false;
                    },
                });
    }

    showPendingFundsDetail(acct: DepositBankAccount) {
        this.pendingDetailAccount = acct;
        this.isShowingPendingFundsDialog = true;

        if (typeof acct.paymentAttempts === 'undefined' || acct.paymentAttempts.length === 0) {
            this.isPendingFundsLoading = true;
            this._bankAccountsService.getDepositAccountPendingFunds(acct.id)
                    .pipe(untilDestroyed(this))
                    .subscribe(
                            (attempts) => {
                                acct.paymentAttempts = attempts;
                                this.isPendingFundsLoading = false;
                            },
                    );
        }
    }

    addBankAccount() {
        this._dialog.open(AddOrgBankAccountDialogComponent, {
            width: MatDialogSizes.MD,
        })
                .afterClosed().subscribe({
            next: (result: AddOrgBankAccountDialogResult) => {
                if (result.success) {
                    this.loadBankAccounts();
                }
            },
        });
    }

    fixPlaidError(account: UnifiedBankAccount) {
        this._plaidService.fixPlaidError(account, () => {
            this.loadBankAccounts();
        });
    }
}
